import {
  Card,
  Flex,
  PanelHeader,
  PanelLayout,
  PanelLayoutTypes,
  PanelSidebar,
  Sidebar,
  SidebarItem,
  SimpleTable,
  Switcher,
  Typography,
} from "djuno-design";
import { useState } from "react";
import { ReactComponent as Logo } from "./../assets/icons/logo.svg";
import { ReactComponent as FaceSmile } from "./../assets/icons/face-smile.svg";
import { Link } from "react-router-dom";
import { panelLayoutsPageUrl } from "../urls";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const SidebarPage = () => {
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [panelType, setPanelType] = useState<PanelLayoutTypes>("mini");
  const [pathname, setPathname] = useState<string>("/item1");

  const handleTogleSidebarLoading = () => {
    setSidebarLoading((prev) => !prev);
  };
  const handleToglePanelType = () => {
    setPanelType((prev) => (prev === "normal" ? "mini" : "normal"));
  };
  const handleChangePathname = () => {
    setPathname((prev) => (prev === "/item1" ? "/item2-hi/item2-1" : "/item1"));
  };

  const sidebarItems: SidebarItem[] = [
    {
      id: 1,
      label: "item1",
      activeConditions: [
        {
          index: 0,
          value: "item1",
        },
      ],
      icon: FaceSmile,
    },
    {
      id: 2,
      label: "item2",
      activeConditions: [
        {
          index: 0,
          value: "item2",
        },
      ],
      children: [
        {
          id: "2-0",
          label: "item2-0",
          activeConditions: [
            {
              index: 1,
              value: "item2-0",
            },
          ],
        },
        {
          id: "2-1",
          label: "item2-1",
          activeConditions: [
            {
              index: 1,
              value: "item2-1",
            },
          ],
          children: [
            {
              id: "2-1-0",
              label: "item2-1-0",
              activeConditions: [
                {
                  index: 2,
                  value: "item2-1-0",
                },
              ],
            },
            {
              id: "2-1-1",
              label: "item2-1-1",
              activeConditions: [
                {
                  index: 2,
                  value: "item2-1-1",
                },
              ],
              children: [],
            },
          ],
        },
        {
          id: "2-2",
          label: "item2-2",
          activeConditions: [
            {
              index: 1,
              value: "item2-2",
            },
          ],
          children: [
            {
              id: "2-2-0",
              label: "item2-2-0",
              activeConditions: [
                {
                  index: 2,
                  value: "item2-2-0",
                },
              ],
            },
            {
              id: "2-2-1",
              label: "item2-2-1",
              activeConditions: [
                {
                  index: 2,
                  value: "item2-2-1",
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: 3,
      label: "item3",
      activeConditions: [
        {
          index: 0,
          value: "item3",
        },
      ],
      link: "/item3",
      children: [
        {
          id: "3-0",
          label: "item3-0",
          activeConditions: [
            {
              index: 1,
              value: "item3-0",
            },
          ],
        },
        {
          id: "3-1",
          label: "item3-1",
          activeConditions: [
            {
              index: 1,
              value: "item3-1",
            },
          ],
        },
      ],
    },
  ];

  const ExampleLoadingController = () => {
    return (
      <Flex items="center" className="gap-1">
        <Text size="xs">Loading?</Text>
        <Switcher onChange={handleTogleSidebarLoading} value={sidebarLoading} />
      </Flex>
    );
  };
  const ExamplesControler = () => {
    return (
      <Flex items="center" className="gap-4 overflow-x-auto pl-2 md:pl-0">
        <Flex items="center" className="gap-1">
          <Text size="xs">mini?</Text>
          <Switcher
            onChange={handleToglePanelType}
            value={panelType === "mini"}
          />
        </Flex>
        <Flex items="center" className="gap-1">
          <Text size="xs">change selected?</Text>
          <Switcher
            onChange={handleChangePathname}
            value={pathname === "/item2"}
          />
        </Flex>
        <ExampleLoadingController />
      </Flex>
    );
  };
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Sidebar</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Sidebar component in Djuno Design provides a flexible and
            customizable vertical navigation panel. It supports dynamic menu
            items, sub-items, and interactive states such as hover effects and
            active indicators. This component is designed to enhance user
            navigation by visually separating main items and providing
            contextual options through sub-items. It also allows for a
            responsive design, adapting to different screen sizes.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Sidebar component offers several customization options to fit
            various use cases:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Items and Sub-items
                </Text>
                : Display a list of primary navigation items and optional
                sub-items for additional content or actions.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Active and Hover States
                </Text>
                : Highlight the active item based on the URL segments or hover
                state, helping users identify their current location within the
                app.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Loading Modes
                </Text>
                : Customize the sidebar's loading behavior, either showing a
                skeleton or a loading spinner for a smoother user experience.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Size Variants
                </Text>
                : Choose between normal and mini sidebar sizes, adapting the
                width and icon spacing according to your design needs.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="gap-1 mt-8 w-full">
          <Flex items="center" justify="between" className="w-full">
            <Title level={5} className="mt-5">
              Examples
            </Title>
          </Flex>

          <Flex direction="col" className="gap-8 w-full">
            <ExpandableCard
              description={
                <Text size="sm" className="whitespace-nowrap">
                  Basic usage
                </Text>
              }
              setting={<ExampleLoadingController />}
              codeViewerProps={{
                code: SidebarExample1,
              }}
            >
              <Flex direction={{ default: "col", md: "row" }} className="gap-5">
                <div className="h-96 w-52 md:w-60 border border-slate-300 rounded-md">
                  <Sidebar
                    type={panelType}
                    items={sidebarItems}
                    subItems={sidebarItems}
                    segments={["item3"]}
                    loading={sidebarLoading}
                    loadingMode="skeleton"
                  />
                </div>
                <div className="h-96 w-52 md:w-60 border border-slate-300 rounded-md">
                  <Sidebar
                    type={panelType}
                    items={sidebarItems}
                    segments={["item1"]}
                    loading={sidebarLoading}
                    loadingMode="elastic"
                  />
                </div>
              </Flex>
            </ExpandableCard>
            <ExpandableCard
              description={
                <Text size="sm">
                  Combine with{" "}
                  <Typography.Link size="sm">
                    <Link to={panelLayoutsPageUrl}>PanelLayout</Link>
                  </Typography.Link>
                </Text>
              }
              setting={<ExamplesControler />}
              codeViewerProps={{
                code: SidebarExample2,
              }}
            >
              <PanelLayout
                type={panelType}
                pathname={pathname}
                className="h-96 w-full border border-slate-300 rounded-md overflow-hidden"
                renderSidebar={({ segments, isShowSidebar, type }) => (
                  <PanelSidebar
                    isShowSidebar={isShowSidebar}
                    type={type}
                    sidebarHeader={
                      <div className="flex items-center gap-1 px-1">
                        <Logo />
                        <Text size="xs">djuno-design</Text>
                      </div>
                    }
                  >
                    <Sidebar
                      type={type}
                      items={sidebarItems}
                      segments={segments}
                      loading={sidebarLoading}
                      loadingMode="skeleton"
                    />
                  </PanelSidebar>
                )}
                renderHeader={(headerProps) => (
                  <PanelHeader {...headerProps} mobileIcon={<Logo />}>
                    <Text size="xs">header</Text>
                  </PanelHeader>
                )}
                enableGoToTopAfterScroll={false}
                enableGoToTopAfterChangeRoute={false}
              >
                <iframe src="/" className="w-full h-96" title="djuno-design" />
              </PanelLayout>
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* items */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">items</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        An array of objects representing the main items in the
                        sidebar. Each item can have a label, icon, link, and
                        more.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "ISidebar.ts#L34"}
                        size="xs"
                        code
                      >
                        {`SidebarItem[]`}
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">{`[]`}</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* segments */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">segments</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Array of URL segments to determine the active state of
                        sidebar items.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`string[]`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* subItems */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">subItems</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Optional array of sub-items that appear under specific
                        main items.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "ISidebar.ts#L34"}
                        size="xs"
                        code
                      >
                        {`SidebarItem[]`}
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">{`[]`}</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* loading */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loading</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Whether the sidebar is in a loading state.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* loadingMode */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loadingMode</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Determines how the sidebar appears while loading: either
                        a spinner or skeleton loader.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "ISidebar.ts#L48"}
                        size="xs"
                        code
                      >
                        SidebarLoadingModes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">simple</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* type */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">type</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The size of the sidebar. normal is the default size,
                        while mini reduces the width for a more compact layout.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "IPanelLayouts.ts#L21"}
                        size="xs"
                        code
                      >
                        PanelLayoutTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">normal</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Optional content to be rendered below the sidebar items.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* navItemHeight */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">navItemHeight</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">The height of each element.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        number
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">36px</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>

        {/* Types */}
        {/* <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            Types
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API"></Card>
          </Flex>
        </Flex> */}
      </Flex>
    </div>
  );
};

const SidebarExample1 = `<Flex direction={{ default: "col", md: "row" }} className="gap-5">
<div className="h-96 w-52 md:w-60 border border-slate-300 rounded-md">
  <Sidebar
    type={panelType}
    items={sidebarItems}
    subItems={sidebarItems}
    segments={["item3"]}
    loading={sidebarLoading}
    loadingMode="skeleton"
  />
</div>
<div className="h-96 w-52 md:w-60 border border-slate-300 rounded-md">
  <Sidebar
    type={panelType}
    items={sidebarItems}
    segments={["item1"]}
    loading={sidebarLoading}
    loadingMode="elastic"
  />
</div>
</Flex>`;
const SidebarExample2 = `import {PanelLayout} from "djuno-design"; 
<PanelLayout
  type={panelType}
  pathname={pathname}
  className="h-96 w-full border border-slate-300 rounded-md overflow-hidden"
  renderSidebar={({ segments, isShowSidebar, type }) => (
    <PanelSidebar
      isShowSidebar={isShowSidebar}
      type={type}
      sidebarHeader={
        <div className="flex items-center gap-1 px-1">
          <Logo />
          <Text size="xs">djuno-design</Text>
        </div>
      }
    >
      <Sidebar
        type={type}
        items={sidebarItems}
        segments={segments}
        loading={sidebarLoading}
        loadingMode="skeleton"
      />
    </PanelSidebar>
  )}
  renderHeader={(headerProps) => (
    <PanelHeader {...headerProps} mobileIcon={<Logo />}>
      <Text size="xs">header</Text>
    </PanelHeader>
  )}
  enableGoToTopAfterScroll={false}
  enableGoToTopAfterChangeRoute={false}
>
  <iframe src="/" className="w-full h-96" title="djuno-design" />
</PanelLayout>`;

export default SidebarPage;
